import ToolbarButton from "../toolbar/ToolbarButton";
import { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { DrawingContext, DrawingMode } from "../../contexts/DrawingContext";
import { IgnitionRegimeContext } from "../../contexts/IgnitionRegimeContext";
import { Ignition } from "../../types/ignitionRegime";
import { useDeleteIgnition } from "../../api/ignitionRegimes";
import IgnitionPopover from "../IgnitionPopover";
import { useSimulation } from "../../contexts/SimulationContext";
import { useMap } from "../../contexts/MapContext";
import { Filter } from "../layerControl/FilterModal";
import { applyFilters } from "../../utils/layerFilters";
import { mapEventScreenPoint } from "../../utils/uiZoom";

const IgnitionsLayer = (id: string, name: string, filters: Filter[] = []) => {
  const { selectedRegime } = useContext(IgnitionRegimeContext);
  const { drawingMode } = useContext(DrawingContext);
  const { currentSavedSimId } = useSimulation();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;
  const regimeId = selectedRegime?.id || 0;
  const { mutate: deleteIgnition } = useDeleteIgnition(projectIdNumber, regimeId);

  const [geojson, setGeojson] = useState<any>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [stale, setStale] = useState(false);

  const hasSeenInitialSelectedRegime = useRef(false);
  const { map } = useMap();

  const [selectedIgnition, setSelectedIgnition] = useState<Ignition | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!selectedRegime || !selectedRegime.ignitions || selectedRegime.ignitions.length === 0) {
      setGeojson({
        type: "FeatureCollection",
        features: [],
      });
      setStale(true);
      return;
    }

    const baseGeojson = {
      type: "FeatureCollection",
      features: selectedRegime.ignitions,
    };

    // Apply user filters
    const filtered = applyFilters(baseGeojson, filters);

    setGeojson(filtered);
    setStale(true);
  }, [selectedRegime, filters]);

  useEffect(() => {
    if (!selectedRegime) return;

    // Keep default OFF on initial page load.
    // After the initial selection is established, any subsequent regime changes should auto-enable.
    if (!hasSeenInitialSelectedRegime.current) {
      hasSeenInitialSelectedRegime.current = true;
      return;
    }

    setIsVisible(true);
    setStale(true);
  }, [selectedRegime?.id]);

  useEffect(() => {
    if (!map) return;
    const source = (map as any).getSource(id);
    if (!source || typeof source.setData !== "function") return;
    const data = isVisible ? geojson : { type: "FeatureCollection", features: [] };
    if (data) source.setData(data);
  }, [geojson, isVisible, map]);

  useEffect(() => {
    if (drawingMode !== DrawingMode.IGNITION) return;
    setIsVisible(true);
    setStale(true);
  }, [drawingMode]);

  useEffect(() => {
    if (currentSavedSimId) {
      setIsVisible(false);
      setStale(true);
    }
  }, [currentSavedSimId]);

  const displayedGeojson = isVisible
    ? geojson
    : { type: "FeatureCollection", features: [] };

  const pointLayerStyle = {
    id: `${id}-points`,
    type: "circle",
    filter: ["==", ["geometry-type"], "Point"],
    paint: {
      "circle-radius": 6,
      "circle-color": "#73ff00",
      "circle-opacity": 0.75,
    },
    layout: {
      visibility: isVisible ? "visible" : "none",
    },
  };

  const lineLayerStyle = {
    id: `${id}-lines`,
    type: "line",
    filter: ["==", ["geometry-type"], "LineString"],
    paint: {
      "line-color": "#73ff00",
      "line-width": 5,
      "line-opacity": 0.75,
    },
    layout: {
      visibility: isVisible ? "visible" : "none",
    },
  };

  const polygonLayerStyle = {
    id: `${id}-polygons`,
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    paint: {
      "fill-color": "#73ff00",
      "fill-opacity": 0.75,
    },
    layout: {
      visibility: isVisible ? "visible" : "none",
    },
  };

  const handleToggle = () => {
    setIsVisible(!isVisible);
    setStale(true);
  };

  const handleClick = (e: any) => {
    if (!e.features || e.features.length === 0) return;

    const feature = e.features[0];
    const ignitionId = feature.properties?.id;

    const ignition = selectedRegime?.ignitions.find((i) => i.id === ignitionId);
    if (!ignition) return;

    setSelectedIgnition(ignition);
    setPopoverPosition(mapEventScreenPoint(e));
  };

  const handleClosePopover = () => {
    setSelectedIgnition(null);
    setPopoverPosition(null);
  };

  const handleDeleteIgnition = (ignition: Ignition) => {
    deleteIgnition(ignition.id);
    handleClosePopover();
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      enabled={!!geojson}
      toggledOn={isVisible}
      icon={"bolt"}
      onClick={handleToggle}
    />
  );

  const popover = (
    <IgnitionPopover
      ignition={selectedIgnition}
      anchorPosition={popoverPosition}
      onClose={handleClosePopover}
      onDelete={handleDeleteIgnition}
    />
  );

  return {
    id,
    name,
    source: displayedGeojson,
    rawSource: geojson,
    layerStyle: [pointLayerStyle, lineLayerStyle, polygonLayerStyle],
    toolbarButton,
    active: isVisible,
    stale,
    setStale,
    interactiveLayerIds: [`${id}-points`, `${id}-lines`, `${id}-polygons`],
    onClick: handleClick,
    popover,
  };
};

export default IgnitionsLayer;
